import { identifyUser, removeIdentity } from "utils/hubspot";

import { UserBuddyStatus } from "pages/employee/types";

import { Organization, RegisterAccountPayload, User } from "../types";
import { apiClient } from "../../utils/apiClient";

const localStorageTokenKey = "__auth-token";
const localStorageDataKey = "__auth-data";
const localStorageRoomIdKey = "__auth-roomId";
const localStorageRoomTemplateIdKey = "__auth-roomTemplateId";
export const localStorageOrgSlugKey = "__auth-orgSlug";
const localStorageGameSlugKey = "__auth-gameSlug";
const localStorageUserDashboardKey = "__auth-userDashboard";
const localStorageOrgStripeEnabled = "__auth-orgOrderDeprecated";
export const localStorageShowConfirmNotifyBuddy = "__userDashboard-showConfirmNotifyBuddy";

const userLocaleStorageItems: string[] = [
	localStorageTokenKey,
	localStorageDataKey,
	localStorageRoomIdKey,
	localStorageRoomTemplateIdKey,
	localStorageOrgSlugKey,
	localStorageGameSlugKey,
	localStorageUserDashboardKey,
	localStorageOrgStripeEnabled,
];

const localStorageAdminTokenKey = "@ad__auth-token";
const localStorageAdminDataKey = "@ad__auth-data";

type LoginResponse = {
	data: {
		user: User;
		token: string;
		room: {
			roomId: string;
			roomTemplateId: string;
			organization: {
				slug: string;
			};
		};
		organizationSlug: string;
		organization: {
			slug: string;
		};
		gameSlug: string;
	};
};

type AdminLoginResponse = {
	data: {
		user: User;
		token: string;
		organization: {
			slug: string;
			isStripeEnabled: boolean;
		};
	};
};

type AdminRegisterResponse = {
	data: {
		user: User;
		organization: {
			slug: string;
			isStripeEnabled: boolean;
		};
		token: string;
	};
};

type AdminUserResponse = {
	data: {
		user: User;
		organization: Organization;
	};
};

export async function login(data: { email: string; password: string }) {
	const response = await apiClient("auth/login", {
		data,
	});
	if (response.data.room?.users) return handleLoginResponseForGameRoom(response);
	return handleLoginResponseForUserDashboard(response);
}

export async function passwordlessLogin(data: { roomTemplateId: string; token: string }) {
	const response = await apiClient("auth/login/passwordless", {
		data,
	});
	if (response.data.room?.users) return handleLoginResponseForGameRoom(response);
	return handleLoginResponseForUserDashboard(response);
}

export async function fetchRoom(data: { roomId: string }) {
	const response = await apiClient(`rooms/${data.roomId}`, { token: getToken() });
	return handleLoginRoomSetting(response);
}

export const adminLogin = async (data: { username: string; password: string }) => {
	const response = await apiClient("auth/admin/login", {
		data,
	});
	return handleAdminLoginResponse(response);
};

export const adminRegister = async (data: RegisterAccountPayload) => {
	const response = await apiClient("auth/admin/register", {
		data,
	});
	return handleAdminRegisterResponse(response);
};

export const resendToken = async () => {
	const token = getAdminToken();
	if (token) {
		await apiClient("auth/verify-email/resend", { token, data: {} });
	}
};

const handleActivateResponse = async (response: any) => {
	await fetchAccount();
	window.location.href = `/admin/onboarding/user`;
};

export const activate = async (data: { code: string; email: string }) => {
	const token = getAdminToken();
	const response = await apiClient("auth/otp-verification-email", { token, data });
	return handleActivateResponse(response);
};

export const resendCode = async (data: { email: string }) => {
	const token = getAdminToken();
	if (token) {
		await apiClient("auth/otp-verification-email/resend", { token, data });
	}
};

export const verifyAdminToken = async () => {
	const token = getAdminToken();
	if (token) {
		return apiClient(`auth/token-validity`, { token });
	}
	return null;
};

export const fetchAccount = async () => {
	if (window.localStorage.getItem(localStorageAdminTokenKey)) {
		const response = await apiClient("auth/admin/info", { token: getAdminToken() });
		return handleGetUserInfoResponse(response);
	}

	throw Error("Invalid token");
};

export const verifyToken = async () => {
	if (window.localStorage.getItem(localStorageTokenKey)) {
		await apiClient(`auth/token-validity`, { token: getToken() });
	}
};

const handleAdminLoginResponse = ({ data }: AdminLoginResponse) => {
	window.localStorage.setItem(localStorageAdminTokenKey, data.token);
	window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(data.user));
	window.localStorage.setItem(localStorageOrgStripeEnabled, String(data.organization.isStripeEnabled));
	identifyUser({ email: data.user.email });
	if (!data.user.isEmailVerified) {
		window.location.href = `/admin/activate`;
	} else {
		window.location.href = `/admin/dashboard`;
	}
	return data;
};

const handleAdminRegisterResponse = ({ data }: AdminRegisterResponse) => {
	window.localStorage.setItem(localStorageAdminTokenKey, data.token);
	window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(data.user));
	window.localStorage.setItem(localStorageOrgStripeEnabled, String(data.organization.isStripeEnabled));
	identifyUser({ email: data.user.email });
	if (!data.user.isEmailVerified) {
		window.location.href = `/admin/activate`;
	}
	return data;
};

const handleGetUserInfoResponse = ({ data }: AdminUserResponse) => {
	if (data.user.isEmailVerified) {
		window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(data.user));
	}
	return data;
};

function handleLoginResponseForGameRoom({ data }: LoginResponse) {
	window.localStorage.setItem(localStorageTokenKey, data.token);
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(data.user));
	if (data.room?.roomId) {
		window.localStorage.setItem(localStorageRoomIdKey, data.room?.roomId);
	}
	if (data.room?.roomTemplateId) {
		window.localStorage.setItem(localStorageRoomTemplateIdKey, data.room?.roomTemplateId);
	}
	if (data.room?.organization.slug || data.organizationSlug) {
		window.localStorage.setItem(localStorageOrgSlugKey, data.room?.organization.slug || data.organizationSlug);
	}
	if (data.gameSlug) {
		window.localStorage.setItem(localStorageGameSlugKey, data.gameSlug);
	}
	return data;
}
function handleLoginResponseForUserDashboard({ data }: LoginResponse) {
	window.localStorage.setItem(localStorageTokenKey, data.token);
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(data.user));
	if (data.room?.[0]?.organization?.slug || data.organizationSlug) {
		window.localStorage.setItem(
			localStorageOrgSlugKey,
			data.room?.[0]?.organization?.slug || data.organizationSlug,
		);
	}
	if (data.gameSlug) {
		window.localStorage.setItem(localStorageGameSlugKey, data.gameSlug);
	}
	window.localStorage.setItem(localStorageUserDashboardKey, "user-dashboard");
	window.localStorage.setItem(localStorageShowConfirmNotifyBuddy, "show");
	return data;
}

function handleLoginRoomSetting({ data }: LoginResponse) {
	window.localStorage.setItem(localStorageRoomIdKey, data.room?.roomId);
	window.localStorage.setItem(localStorageRoomTemplateIdKey, data.room?.roomTemplateId);
	return data;
}

export function userLogout() {
	for (let item of userLocaleStorageItems) {
		window.localStorage.removeItem(item);
	}
	removeIdentity(); // Remove hubspotutk cookie
}

export function userLogouttoUserDashboard() {
	window.localStorage.removeItem(localStorageRoomIdKey);
	window.localStorage.removeItem(localStorageRoomTemplateIdKey);
	window.localStorage.removeItem(localStorageGameSlugKey);
}

export function getToken() {
	return window.localStorage.getItem(localStorageTokenKey);
}
export function getRoomId() {
	return window.localStorage.getItem(localStorageRoomIdKey);
}
export function getUser() {
	const json = window.localStorage.getItem(localStorageDataKey);
	if (json) {
		return JSON.parse(json);
	}
	return null;
}

export function getRoomTemplateId() {
	return window.localStorage.getItem(localStorageRoomTemplateIdKey);
}
export function getOrganizationSlug() {
	return window.localStorage.getItem(localStorageOrgSlugKey);
}
export function getOrganizationStripeEnabled() {
	return window.localStorage.getItem(localStorageOrgStripeEnabled);
}
export function getGameSlug() {
	return window.localStorage.getItem(localStorageGameSlugKey);
}
export function getUserDashboardSlug() {
	return window.localStorage.getItem(localStorageUserDashboardKey);
}

export function updateBudyStatus(status: UserBuddyStatus) {
	const user = getUser();
	user.buddyStatus = status;

	// User is not a buddy if the status is rejected
	if (status === UserBuddyStatus.REJECTED) {
		user.isBuddy = false;
	}

	window.localStorage.setItem(localStorageDataKey, JSON.stringify(user));
}

export function updateUserProfile(
	profile: Partial<{ phoneNumber: string; birthDate: string; profilePicture: string }>,
) {
	const user = getUser();
	Object.assign(user, profile);
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(user));
}

export function setGameSlug(gameSlug: string) {
	if (!gameSlug) return;
	return window.localStorage.setItem(localStorageGameSlugKey, gameSlug);
}

export function getAdminUser() {
	const json = window.localStorage.getItem(localStorageAdminDataKey);
	if (json) {
		return JSON.parse(json);
	}
	return null;
}

export function getAdminToken() {
	return window.localStorage.getItem(localStorageAdminTokenKey);
}

export async function adminLogout() {
	const token = getAdminToken();
	window.localStorage.removeItem(localStorageAdminTokenKey);
	window.localStorage.removeItem(localStorageAdminDataKey);
	await apiClient("admin/logout", {
		data: {},
		token,
	});
	removeIdentity(); // Remove hubspotutk cookie
	window.setTimeout(() => {
		window.location.href = "/admin/login";
	}, 500);
}

export async function resetPassword(email: string, orgId?: string) {
	await apiClient("auth/reset-password", {
		data: { email, orgId },
	});
}

export async function resetPasswordConfirm(token: string, password: string) {
	await apiClient("auth/reset-password/confirm", {
		data: { token, password },
	});
}
